import { append, equals, filter, find, findIndex, gte, includes, join, lensPath, map, not, pathOr, prop, remove, set, uniqBy } from "ramda";
import { isEmptyOrNil, isNotEmpty } from "src/utilities/hasValue";
import { KIND_DOCUMENT_ACCESS_CONTROL, V1 } from "src/services/constants";
import { mkObject } from 'src/services/objects/build'
import { isString } from "src/utilities/types";
import { getNamespaceName } from "src/services/objects/namespaces";

const mkDocumentAccessControl = (role: string, product: string) => {
    return {
        object_meta: mkObject(`${role}_${product}`),
        type_meta: {
            kind: KIND_DOCUMENT_ACCESS_CONTROL,
            api_version: V1
        },
        access_controls: [],
        permitted_access: {},
        persona: role,
        loantype: product,
        created_at: 'now()',
        updated_at: 'now()',
    }
}


class MinimalDocumentArtifacts {
    documentArtifacts: DocumentArtifact[];
    constructor(documentArtifacts: {category: string, artifact: string}[]) {
        const artifacts = uniqBy(prop('artifact'), documentArtifacts)
        this.documentArtifacts = map((d: any) => new DocumentArtifact(d), artifacts)
    }

    getCategory(artifact: string) {
      const document = find((d: DocumentArtifact) => equals(d.artifact(), artifact), this.documentArtifacts)
      return document?.category() || ''
    }

    getArtifact(category: string) {
        const document = find((d: DocumentArtifact) => equals(d.category(), category), this.documentArtifacts)
        return document?.artifact() || ''
    }
}


/*
DocumentArtifact is to handle the data of document_artifacts 
*/
class DocumentArtifact {
    document: any;
    constructor(document: any) {
        this.document = document
    }

    idKey() {
        return pathOr('', ['id'], this.document)
    }

    description() {
        return pathOr('', ['description'], this.document)
    }

    childOf() {
        return pathOr('', ['child_of'], this.document)
    }

    artifact() {
        return pathOr('', ['artifact'], this.document)
    }

    category() {
        return pathOr('', ['category'], this.document)
    }

    options() {
        return pathOr([], ['options'], this.document)
    }
}

class DocumentAccessControl {
    documentAccess: any;
    constructor(documentAccess: any) {
        this.documentAccess = documentAccess
    }

    idKey() {
        return pathOr('', ['id'], this.documentAccess)
    }

    loantype() {
        return pathOr('', ['loantype'], this.documentAccess)
    }

    role() {
        return pathOr('', ['persona'], this.documentAccess)
    }

    accessControls() {
        return pathOr([], ['access_controls'], this.documentAccess)
    }

    permittedAccess() {
        return pathOr({}, ['permitted_access'], this.documentAccess)
    }

    appendAccessContols(document: DocumentArtifact) {
        const accessControls = this.accessControls()
        if (not(includes(document.artifact(), accessControls))) {
            this.documentAccess = set(
                lensPath(['access_controls']),
                append(document.artifact(), accessControls),
                this.documentAccess)
        }
    }

    removeAccessContols(document: DocumentArtifact) {
        const accessControls = this.accessControls()
        const index = findIndex(equals(document.artifact()), accessControls)
        if (gte(index, 0)) {
            this.documentAccess = set(
                lensPath(['access_controls']),
                remove(index, 1, accessControls),
                this.documentAccess)
        }
    }

    appendPermittedAccess(accessKey: string, artifact: string) {
        const accessList = filter((s: any) => isString(s), pathOr([], [accessKey], this.permittedAccess()))
        if (not(includes(artifact, accessList))) {
            this.documentAccess = set(
                lensPath(['permitted_access', accessKey]),
                append(artifact, accessList),
                this.documentAccess)
        }
    }

    removePermittedAccess(accessKey: string, artifact: string) {
        const accessList = filter((s: any) => isString(s), pathOr([], [accessKey], this.permittedAccess()));
        const index = findIndex(equals(artifact), accessList)
        if (gte(index, 0)) {
            this.documentAccess = set(
                lensPath(['permitted_access', accessKey]),
                remove(index, 1, accessList),
                this.documentAccess)
        }
    }

    payload() {
        return {
            ...this.documentAccess,
            access_controls: `{${join(', ', this.accessControls())}}`,
            updated_at: 'now()'
        }
    }

}

class RoleProductDocumentAccess {
    accessControl: DocumentAccessControl;
    documentArtifacts: DocumentArtifact[];
    constructor(accessControl: DocumentAccessControl) {
        this.accessControl = accessControl
    }

    grandAccess(checked: boolean, selectedArtifact: DocumentArtifact) {
        checked ?
            this.accessControl.appendAccessContols(selectedArtifact) :
            this.accessControl.removeAccessContols(selectedArtifact)
    }

    setRequired(checked: boolean, document: DocumentArtifact) {
        checked ? this.accessControl.appendPermittedAccess(
            "require_documents:show",
            document.artifact()) : 
            this.accessControl.removePermittedAccess(
                "require_documents:show",
                document.artifact())
    }

    hasAccessControl(d: DocumentArtifact) {
        return includes(d.artifact(), this.accessControl.accessControls())
    }

    isRequiredDocument(d: DocumentArtifact) {
        const accessList = pathOr([], ["require_documents:show"], this.accessControl.permittedAccess())
        return includes(d.artifact(), accessList)
    }

    isRoleMatch(role: string) {
        return equals(this.accessControl.role(), role)
    }

    isProductMatch(product: string) {
        return equals(this.accessControl.loantype(), product)
    }

    getDocumentArtifactVarMapName() {
        /* Sample varmap schema 
        {
            "loan_products": [
                    {
                        "product_name": "RESIDENTIALMORTGAGE"
                        "document_artifacts": [
                             "lendsmart_sh/appraisal",
                             "lendsmart_sh/home_insurance_documents",
                             "lendsmart_sh/borrowers",
                             "lendsmart_sh/bankstatement"
                        ]
                    }
            ]
        }
        */
        return `${getNamespaceName()}_site_configured_products_document_artifacts`
    }


    getDocumentAccessControlVarMapName() {
        /* Sample varmap schema 
{
  "loan_products": [
    {
      "product_name": "RESIDENTIALMORTGAGE",
      "folders": [
        {
          "child": [],
          "parent": "Tax return statement"
        },
        {
          "child": [
            "Right Side",
            "Left Side"
          ],
          "parent": "Closing"
        },
        {
          "child": [
            "Appraisal",
            "Credit Report",
            "Title Commitment",
            "DU Findings",
            "Flood Certification"
          ],
          "parent": "Closing Conditions"
        }
      ],
      "subproducts": [
        {
          "sub_product_name": "Purchase",
          "document_access_controls": {
            "require_documents:show": [
              "lendsmart_sh/appraisal",
              "lendsmart_sh/home_insurance_documents",
              "lendsmart_sh/borrowers",
              "lendsmart_sh/bankstatement"
            ]
          }
        },
        {
          "sub_product_name": "Refinance",
          "document_access_controls": {
            "require_documents:show": [
              "lendsmart_sh/borrowers",
              "lendsmart_sh/bankstatement"
            ]
          }
        }
      ]
    }
  ]
}
*/
        return `${getNamespaceName()}_site_configured_products_document_access_controls`

    }

}


/*
DocumentsCollection is to handle list of document artifacts  
*/
class DocumentsCollection {
    documentArtifacts: DocumentArtifact[];
    documentAccessControls: DocumentAccessControl[];
    roleProductDocumentAccess: RoleProductDocumentAccess[]
    role: any;
    constructor(documentArtifacts: any[], role: any) {
        // TODO: remove duplicates 
        // for now adding patch filter duplicate duplicates 
        const artifacts = uniqBy(prop('artifact'), documentArtifacts)
        this.documentArtifacts = map((d: any) => new DocumentArtifact(d), artifacts)
        this.role = role
    }

    newDocumentAccess(role: string, product: string) {
        return new DocumentAccessControl(mkDocumentAccessControl(
            role, product)
        )
    }

    setAccessControls(documentAccessControls: any[], loanProducts: any[]) {
        if (isEmptyOrNil(this.documentAccessControls)) {
            this.documentAccessControls = map((c: any) => new DocumentAccessControl(c), documentAccessControls)
            this.roleProductDocumentAccess = map((loanProduct: any) => {
                const documentAccess = find((c: DocumentAccessControl) => {
                    return equals(c.role(), pathOr('', ['name'], this.role)) && equals(c.loantype(), pathOr('', ['product_name'], loanProduct))
                }, this.documentAccessControls)

                const docAccess: any = isNotEmpty(documentAccess) ? documentAccess : this.newDocumentAccess(
                    pathOr('', ['name'], this.role),
                    pathOr('', ['product_name'], loanProduct)
                )
                return new RoleProductDocumentAccess(docAccess)
            }, loanProducts)
        }
    }

    artifacts() {
        return this.documentArtifacts
    }

    getRoleAccess(role: string, product: string) {
        return find(((r: RoleProductDocumentAccess) => {
            return r.isRoleMatch(role) && r.isProductMatch(product)
        }), this.roleProductDocumentAccess) || this.newDocumentAccess(role, product)
    }
}

export {
    DocumentAccessControl,
    DocumentArtifact,
    DocumentsCollection,
    RoleProductDocumentAccess,
    MinimalDocumentArtifacts
}